import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { Bot, Send, Loader2, Database, Sparkles, User, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAIPage() {
  const { api } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    checkAIStatus();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversations]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkAIStatus = async () => {
    try {
      const response = await api.get('/ai/status');
      setAiStatus(response.data);
    } catch (error) {
      console.error('Failed to check AI status:', error);
      setAiStatus({ available: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;

    const userQuestion = query.trim();
    setQuery('');
    
    // Add user message to conversation
    setConversations(prev => [...prev, { type: 'user', content: userQuestion }]);
    setLoading(true);

    try {
      const response = await api.post('/ai/admin/query', { question: userQuestion });
      
      // Add AI response to conversation
      setConversations(prev => [...prev, { 
        type: 'assistant', 
        content: response.data 
      }]);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to process query';
      setConversations(prev => [...prev, { 
        type: 'error', 
        content: errorMessage 
      }]);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const suggestedQueries = [
    "How many students do we have?",
    "List all active courses",
    "Show enrollments by course",
    "Who are our instructors?",
    "Revenue by month",
    "Students with most badges"
  ];

  return (
    <div className="space-y-6" data-testid="admin-ai-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            AI Database Assistant
          </h1>
          <p className="text-muted-foreground">Ask questions about your data in plain English</p>
        </div>
        <Badge variant={aiStatus?.available ? 'default' : 'secondary'}>
          <Sparkles className="h-3 w-3 mr-1" />
          {aiStatus?.model || 'GPT-4o-mini'}
        </Badge>
      </div>

      {!aiStatus?.available && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <CardContent className="py-4">
            <p className="text-yellow-700 dark:text-yellow-300">
              AI service is not configured. Please add the OpenAI API key in the Integrations page.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="card-dashboard">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="h-5 w-5" />
            Query Your Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Suggested Queries */}
          {conversations.length === 0 && (
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-3">Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQueries.map((q, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    size="sm"
                    onClick={() => setQuery(q)}
                    className="text-xs"
                  >
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation History */}
          <div className="space-y-4 mb-4 max-h-[500px] overflow-y-auto">
            {conversations.map((msg, index) => (
              <div key={index} className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : ''}`}>
                {msg.type !== 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className={`flex-1 max-w-[85%] ${msg.type === 'user' ? 'text-right' : ''}`}>
                  {msg.type === 'user' ? (
                    <div className="inline-block bg-primary text-primary-foreground rounded-lg px-4 py-2">
                      {msg.content}
                    </div>
                  ) : msg.type === 'error' ? (
                    <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <p className="text-red-600 dark:text-red-400">{msg.content}</p>
                    </div>
                  ) : (
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                      {/* Summary */}
                      <p className="font-medium">{msg.content.summary}</p>
                      
                      {/* SQL Query */}
                      {msg.content.sql && (
                        <div className="relative">
                          <pre className="bg-slate-900 text-slate-100 rounded p-3 text-xs overflow-x-auto">
                            <code>{msg.content.sql}</code>
                          </pre>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 h-6 w-6"
                            onClick={() => copyToClipboard(msg.content.sql, index)}
                          >
                            {copiedIndex === index ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      )}
                      
                      {/* Results Table */}
                      {msg.content.data?.rows?.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b">
                                {msg.content.data.columns.map((col, i) => (
                                  <th key={i} className="text-left p-2 font-medium">
                                    {col.replace(/_/g, ' ')}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {msg.content.data.rows.slice(0, 10).map((row, rowIndex) => (
                                <tr key={rowIndex} className="border-b border-muted">
                                  {msg.content.data.columns.map((col, colIndex) => (
                                    <td key={colIndex} className="p-2">
                                      {row[col] ?? '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {msg.content.data.count > 10 && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Showing 10 of {msg.content.data.count} results
                            </p>
                          )}
                        </div>
                      )}

                      {msg.content.error && (
                        <p className="text-red-500 text-sm">{msg.content.error}</p>
                      )}
                    </div>
                  )}
                </div>
                {msg.type === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <User className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask a question about your data..."
              disabled={loading || !aiStatus?.available}
              className="flex-1"
              data-testid="ai-query-input"
            />
            <Button 
              type="submit" 
              disabled={loading || !query.trim() || !aiStatus?.available}
              data-testid="ai-query-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
